import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {Alert, Badge, Pagination, Space, Table} from 'antd'
import {getDataOfType} from 'src/lib/utils/get-data-of-type'
import B2BLayout from 'src/react-app/layouts/b2b'
import fetchAPI from 'src/lib/utils/fetch-api'
import {formDataToUrlParams} from 'src/react-app/pages/list-orders/utils'
import {calcTotalWidth, tableRowClick} from 'src/lib/utils/list'
import {PaginationWrapper, TableWrapper} from './styles'
import {useSelector} from 'react-redux'
import {createSelector} from 'reselect'
import dayjs from 'dayjs'
import Filters from './filters'
import './index.css'

const createViewed = createSelector(
  (state: any) => state.viewed,
  counts => counts
)

export default function AdminObjectsListPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const filtersRef = useRef<any>({})
  const [dataSource, setDataSource] = useState<any[]>([])
  const [isDataLoading, setIsDataLoading] = useState(true)
  const [totalRows, setTotalRows] = useState<number>(0)
  const [pageErrors, setPageErrors] = useState<String[]>([])
  const [page, setPage] = useState<number>(1)
  const { counts } = useSelector(createViewed)
  const breadcrumbs = useMemo(() => (
    [{
      href: '/',
      title: 'Отчеты Satels',
    }, {
      href: location.pathname,
      title: 'Список заявок на рекламацию',
    }]
  ), [location.pathname])

  const addDataColumns = useMemo(() => {
    const addDataColumns: any[] = [
      {
        title: 'ID',
        width: 100,
        dataIndex: 'id',
        key: 'id',
        render: (_, item) => getDataOfType(item, 'id', Number, '')
      },
      {
        title: 'Дата обращения',
        width: 200,
        dataIndex: 'created_at',
        key: 'created_at',
        render: key => key && dayjs(key).format('DD.MM.YYYY')
      },
      {
        title: 'Дата запуска заказа',
        width: 200,
        dataIndex: 'date_application',
        key: 'date_application',
        render: key => key && dayjs(key).format('DD.MM.YYYY')
      },
      {
        title: 'Код дилера',
        width: 150,
        dataIndex: 'dealer_code',
        key: 'dealer_code',
      },
      {
        title: 'Номер тех. задания',
        width: 150,
        dataIndex: 'num_tech',
        key: 'num_tech',
      },
      {
        title: 'Новые сообщения',
        width: 150,
        dataIndex: 'not_read',
        key: 'not_read',
        render: val => +val > 0 ? (<Badge count={+val}/>) : '-',
        align: 'center'
      },
      {
        title: 'Адрес объекта',
        width: 200,
        dataIndex: 'address',
        key: 'address',
      },
      {
        title: 'ФИО',
        width: 150,
        dataIndex: 'fio',
        key: 'fio',
      },
      {
        title: 'Телефон',
        width: 150,
        dataIndex: 'phone',
        key: 'phone',
      },
      {
        title: 'Классификация',
        width: 150,
        dataIndex: 'classification',
        key: 'classification',
      },
      {
        title: 'Суть претензии',
        width: 150,
        dataIndex: 'claim',
        key: 'claim',
      },
      {
        title: 'Мероприятия дилера',
        width: 150,
        dataIndex: 'dealer_event',
        key: 'dealer_event',
      },
      {
        title: 'Требования дилера',
        width: 150,
        dataIndex: 'dealer_requirement',
        key: 'dealer_requirement',
      },
      {
        title: 'Статус рекламации',
        width: 150,
        dataIndex: 'status',
        key: 'status',
      }
    ]
    return addDataColumns
  }, [])

  const totalWidth: any = useMemo(() => calcTotalWidth( addDataColumns), [ addDataColumns])
  // @ts-ignore
  const finalColumns = useMemo(() => [].concat(addDataColumns).filter(item => item.title), [addDataColumns])
  const cbTableRowClick = useCallback((item) => item['id'] && navigate(`/claim-requests/${item['id']}`), [history])

  const deleteRecord = useCallback(async (currentId) => {
    try{
      await fetchAPI(`/api/collections/objects/${currentId}`, { method: 'DELETE'})
      setDataSource(dataSource => dataSource.filter(({ id }) => id !== currentId))
    }catch(error){
      console.error(error)
    }
  }, [])

  const getData = useCallback(async (value, addUrlParams?: string) => {
    const urlParams = formDataToUrlParams(value)
    const pageErrors: string[] = []
    const [collections] = await Promise.all([
      fetchAPI(`/api/claims_request?${urlParams}&${addUrlParams}`),
    ])

    const dataSource: any[] = getDataOfType(collections, 'data.data', Array, [])
    const newDataSource = dataSource.map(item => ({
      ...item,
      deleteRecord,
    }))

    // const totalRows = getDataOfType(collections, 'data.total-rows', String, 0)
    setTotalRows( Number(collections?.['data']?.['total-rows']) )
    setDataSource(newDataSource)
    setPageErrors(pageErrors)
    setIsDataLoading(false)
  }, [deleteRecord])

  const onPagination = useCallback(async ( page, pageSize) => {
    setPage(page)
    const urlParams = `page=${page}&pageSize=${pageSize}`
    setIsDataLoading(true)
    await getData(filtersRef.current, urlParams)
    setIsDataLoading(false)
  }, [getData])

  useEffect(() => {
    const urlParams = `page=${page}&pageSize=10`
    page === 1 && getData(filtersRef.current,urlParams)
  }, [counts, page, getData])

  const rowClassName = useCallback(record => {
    return record?.['is_read'] ? 'table-row-dark' :  'table-row-light'
  }, [])

  const onFilter = useCallback(value => {
    Object.assign(filtersRef.current, value)
    getData(value)
  }, [getData])

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      <Space align="baseline">
        <Filters onChange={onFilter} />
      </Space>
      {pageErrors.length > 0 ? (
        <div style={{ marginBottom: 40 }}>
          <Alert
            message="При выполнении операции возникла ошибка:"
            showIcon
            type="error"
            description={pageErrors.join('. ')}
          />
        </div>
      ) : null}
      <TableWrapper>
        <Table
          columns={finalColumns}
          rowClassName={rowClassName}
          size="small"
          dataSource={dataSource}
          scroll={{ x: totalWidth, y: '72vh' }}
          pagination={false}
          loading={isDataLoading}
          onHeaderRow={(column: any) => ({
            style: {
              fontSize: column.key && column.key.substr(-3) === '-at' ? 9 : 13,
            },
          })}
          onRow={record => ({ onClick: event => tableRowClick(event, record, cbTableRowClick) })}
        />
      </TableWrapper>
      {Boolean(totalRows) && <PaginationWrapper>
        <Pagination defaultCurrent={1} pageSize={10} total={totalRows} onChange={onPagination} showSizeChanger={false} />
      </PaginationWrapper>}
    </B2BLayout>
  )
}
